import { useState } from 'react';
import {
  AppDateField,
  AppEmptyState,
  AppFormDialog,
  AppScreenScaffold,
  AppSectionCard,
  AppSectionDivider,
  AppSectionRow,
  AppTextField,
  AppTopBar,
  AppUndoSnackbar,
  IconButton,
} from '@core/designsystem';
import type { ClientDetailActions, ClientDetailState } from './useClientDetail';
import './clients.css';

type Field = 'name' | 'phone' | 'email' | 'address' | 'notes';

const FIELD_LABEL: Record<Field, string> = {
  name: 'Имя или наименование',
  phone: 'Телефон',
  email: 'Почта',
  address: 'Адрес',
  notes: 'Заметки',
};

/**
 * Карточка клиента — «Структура» §8.
 *
 * Звонок и письмо — системными средствами телефона: `tel:` и `mailto:`. Своего набора
 * номера мы не делаем и делать не будем — юрист звонит тем же приложением, что и всегда,
 * и звонок попадает в его же журнал.
 */
export function ClientDetailScreen({
  state,
  actions,
  onBack,
  onOpenCase,
  onOpenDocument,
}: {
  state: ClientDetailState;
  actions: ClientDetailActions;
  onBack: () => void;
  onOpenCase: (caseId: string) => void;
  onOpenDocument: (documentId: string) => void;
}) {
  const [editing, setEditing] = useState<Field | null>(null);
  const [birthday, setBirthday] = useState(false);
  const [creatingCase, setCreatingCase] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (state.notFound) {
    return (
      <AppScreenScaffold topBar={<AppTopBar title="Клиент" onBack={onBack} />}>
        <AppEmptyState icon="error" text="Клиент не найден" />
      </AppScreenScaffold>
    );
  }

  const valueOf = (field: Field) =>
    field === 'name'
      ? state.name
      : field === 'phone'
        ? (state.phone ?? '')
        : field === 'email'
          ? (state.email ?? '')
          : field === 'address'
            ? (state.address ?? '')
            : (state.notes ?? '');

  const save = (field: Field, value: string) => {
    if (field === 'name') actions.setName(value);
    if (field === 'phone') actions.setPhone(value);
    if (field === 'email') actions.setEmail(value);
    if (field === 'address') actions.setAddress(value);
    if (field === 'notes') actions.setNotes(value);
  };

  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title={state.name}
          subtitle={state.birthLine ?? undefined}
          onBack={onBack}
          actions={
            <IconButton icon="delete" label="Удалить" onClick={() => setConfirmDelete(true)} />
          }
        />
      }
      overlay={
        <>
          {/*
            Удаление клиента спрашивают, а не делают сразу.
            Клиент — общая запись: он стоит в нескольких делах, и нажатие на корзину
            вычёркивает его из всех. Юрист приходит сюда из дела, чтобы **отвязать**
            клиента от него, — и это разные действия. Замечание с устройства
            09.09.2026; отвязка теперь названа словом в самом деле («Убрать клиента»).
          */}
          {confirmDelete ? (
            <AppFormDialog
              title="Удалить клиента?"
              onDismiss={() => setConfirmDelete(false)}
              confirm={{
                label: 'Удалить',
                onClick: () => {
                  void actions.remove();
                  setConfirmDelete(false);
                },
              }}
            >
              <p className="clients__warning">
                Он исчезнет из всех дел, где указан. Сами дела и их документы останутся.
                Чтобы убрать клиента только из одного дела, откройте дело и нажмите
                «Убрать клиента».
              </p>
            </AppFormDialog>
          ) : null}

          {editing ? (
            <TextDialog
              title={FIELD_LABEL[editing]}
              value={valueOf(editing)}
              onDismiss={() => setEditing(null)}
              onConfirm={(value) => {
                save(editing, value);
                setEditing(null);
              }}
            />
          ) : null}

          {birthday ? (
            <BirthdayDialog
              value={state.birthDate}
              onDismiss={() => setBirthday(false)}
              onConfirm={(value) => {
                actions.setBirthDate(value);
                setBirthday(false);
              }}
            />
          ) : null}

          {creatingCase ? (
            <NewCaseDialog
              clientName={state.name}
              onDismiss={() => setCreatingCase(false)}
              onCreate={(title) => {
                void actions.createCase(title);
                setCreatingCase(false);
              }}
            />
          ) : null}

          {state.isDeleted ? (
            <AppUndoSnackbar
              text="Клиент удалён"
              onUndo={() => void actions.undoDelete()}
              onExpire={() => {
                actions.deletionShown();
                onBack();
              }}
            />
          ) : null}
        </>
      }
    >
      <AppSectionCard title="Контакты">
        {state.phone ? (
          // Ссылка, а не кнопка: звонок открывает системное приложение телефона.
          <a className="client__link" href={`tel:${state.phone}`}>
            <AppSectionRow icon="call" title={state.phone} subtitle="Позвонить" />
          </a>
        ) : (
          <AppSectionRow icon="add" title="Добавить телефон" action onClick={() => setEditing('phone')} />
        )}
        <AppSectionDivider />
        {state.email ? (
          <a className="client__link" href={`mailto:${state.email}`}>
            <AppSectionRow icon="mail" title={state.email} subtitle="Написать" />
          </a>
        ) : (
          <AppSectionRow icon="add" title="Добавить почту" action onClick={() => setEditing('email')} />
        )}
        <AppSectionDivider />
        <AppSectionRow
          icon="edit"
          title="Изменить контакты"
          action
          onClick={() => setEditing('phone')}
        />
      </AppSectionCard>

      <AppSectionCard title="О клиенте">
        <AppSectionRow icon="person" title={state.name} subtitle="Имя" onClick={() => setEditing('name')} />
        <AppSectionDivider />
        <AppSectionRow
          icon="calendar_month"
          title={state.birthLine ?? 'Добавить дату рождения'}
          subtitle={state.birthLine ? 'День рождения' : undefined}
          action={!state.birthLine}
          onClick={() => setBirthday(true)}
        />
        <AppSectionDivider />
        <AppSectionRow
          icon={state.address ? 'account_balance' : 'add'}
          title={state.address ?? 'Добавить адрес'}
          subtitle={state.address ? 'Адрес' : undefined}
          action={!state.address}
          onClick={() => setEditing('address')}
        />
        <AppSectionDivider />
        <AppSectionRow
          icon={state.notes ? 'description' : 'add'}
          title={state.notes ?? 'Добавить заметки'}
          subtitle={state.notes ? 'Заметки' : undefined}
          action={!state.notes}
          onClick={() => setEditing('notes')}
        />
      </AppSectionCard>

      <AppSectionCard title="Дела">
        {state.activeCases.map((item, index) => (
          <div key={item.id}>
            {index > 0 ? <AppSectionDivider /> : null}
            <AppSectionRow
              icon="folder"
              title={item.title}
              subtitle={item.subtitle}
              onClick={() => onOpenCase(item.id)}
            />
          </div>
        ))}
        {state.activeCases.length > 0 ? <AppSectionDivider /> : null}
        <AppSectionRow icon="add" title="Завести дело" action onClick={() => setCreatingCase(true)} />
      </AppSectionCard>

      {state.closedCases.length > 0 ? (
        <AppSectionCard title="Завершённые дела">
          {state.closedCases.map((item, index) => (
            <div key={item.id}>
              {index > 0 ? <AppSectionDivider /> : null}
              <AppSectionRow
                icon="folder"
                title={item.title}
                subtitle={item.subtitle}
                onClick={() => onOpenCase(item.id)}
              />
            </div>
          ))}
        </AppSectionCard>
      ) : null}

      {state.documents.length > 0 ? (
        <AppSectionCard title="Документы клиента">
          {state.documents.map((item, index) => (
            <div key={item.id}>
              {index > 0 ? <AppSectionDivider /> : null}
              <AppSectionRow
                icon="picture_as_pdf"
                title={item.title}
                subtitle={item.meta}
                onClick={() => onOpenDocument(item.id)}
              />
            </div>
          ))}
        </AppSectionCard>
      ) : null}
    </AppScreenScaffold>
  );
}

function TextDialog({
  title,
  value,
  onDismiss,
  onConfirm,
}: {
  title: string;
  value: string;
  onDismiss: () => void;
  onConfirm: (value: string) => void;
}) {
  const [text, setText] = useState(value);

  return (
    <AppFormDialog
      title={title}
      onDismiss={onDismiss}
      confirm={{ label: 'Готово', onClick: () => onConfirm(text) }}
    >
      <AppTextField value={text} onChange={setText} label={title} autoFocus />
    </AppFormDialog>
  );
}

/** Дата рождения: от неё считается возраст и отметка в календаре. */
function BirthdayDialog({
  value,
  onDismiss,
  onConfirm,
}: {
  value: string | null;
  onDismiss: () => void;
  onConfirm: (value: string | null) => void;
}) {
  const [date, setDate] = useState(value ?? '');

  return (
    <AppFormDialog
      title="День рождения"
      onDismiss={onDismiss}
      dismissLabel="Убрать"
      confirm={{ label: 'Готово', onClick: () => onConfirm(date || null) }}
    >
      <AppDateField value={date} onChange={setDate} label="Дата рождения" />
    </AppFormDialog>
  );
}

/** Дело прямо из карточки клиента: так его чаще всего и заводят. */
function NewCaseDialog({
  clientName,
  onDismiss,
  onCreate,
}: {
  clientName: string;
  onDismiss: () => void;
  onCreate: (title: string) => void;
}) {
  const [title, setTitle] = useState('');

  return (
    <AppFormDialog
      title="Новое дело"
      onDismiss={onDismiss}
      confirm={{
        label: 'Создать',
        enabled: title.trim().length > 0,
        onClick: () => onCreate(title),
      }}
    >
      <AppTextField
        value={title}
        onChange={setTitle}
        label="Название дела"
        placeholder={clientName}
        autoFocus
      />
    </AppFormDialog>
  );
}
